import { useRef, useState, useEffect } from 'react';

import SliderComponent from 'rc-slider';
import Box from '../../Layout/Box/Box';
import styles from './RangeSlider.module.scss';
import 'rc-slider/assets/index.css';
import OutsideClickHandler from '../../../vendor/OutsideClickHandler';
import Icon from '../../DataDisplay/Icon/Icon';
import Button from '../Button/Button';

export type RangeSliderProps<T> = {
  name: T;
  min?: number;
  max?: number;
  label?: string;
  value: number[];
  error?: string | null;
  disabled?: boolean;
  index?: number;
  borderColor?: string;
  id?: string;
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange: (value: number | number[]) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

const RangeSlider = <T extends string>(props: RangeSliderProps<T>): React.JSX.Element => {
  const {
    name,
    min = 0,
    max = 100,
    label,
    value,
    error = null,
    disabled = false,
    borderColor,
    id,
    onFocus,
    onChange = () => {},
  } = props;

  const [currentLabel, setCurrentLabel] = useState(label);
  const [rawMin, setRawMin] = useState(value[0].toString());
  const [rawMax, setRawMax] = useState(value[1].toString());
  const [isDropDownVisible, setIsDropDownVisible] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  const inputEl = useRef<HTMLInputElement>(null);
  const inputLabelArea = useRef<HTMLDivElement>(null);

  const handleChange = (val: number | number[]): void => {
    const newValue = Array.isArray(val) ? val : [val];
    setTempValue(newValue);

    setRawMin(new Intl.NumberFormat('en-US').format(newValue[0]).toString());
    setRawMax(new Intl.NumberFormat('en-US').format(newValue[1]).toString());
  };

  const formatValue = (val: number) => {
    const formatted = new Intl.NumberFormat('en-US').format(val);
    return formatted.length > 5 ? `${formatted.slice(0, 5)}...` : formatted;
  };

  const handleOnApply = () => {
    setCurrentLabel(`$${formatValue(tempValue[0])} to $${formatValue(tempValue[1])}`);
    onChange(tempValue);
    setIsDropDownVisible(false);
  };

  const handleReset = () => {
    setTempValue([min, max]);
    onChange([min, max]);
    setCurrentLabel(label);
  };

  const toggleMenuVisibility = () => {
    if (disabled) return;
    setIsDropDownVisible(!isDropDownVisible);
  };

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow numbers, dash, and period
    const valueWithoutInvalidChars = e.target.value.replace(/[^0-9-.]/g, '');
    setRawMin(valueWithoutInvalidChars);
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow numbers, dash, and period
    const valueWithoutInvalidChars = e.target.value.replace(/[^0-9-.]/g, '');
    setRawMax(valueWithoutInvalidChars);
  };

  const handleMinInputBlur = () => {
    const parsedValue = parseFloat(rawMin);
    const valueToSet = Number.isNaN(parsedValue) ? 0 : parsedValue;
    setTempValue([valueToSet, tempValue[1]]);
    // Format for display
    setRawMin(new Intl.NumberFormat('en-US').format(valueToSet).toString());
  };

  const handleMaxInputBlur = () => {
    const parsedValue = parseFloat(rawMax);
    const valueToSet = Number.isNaN(parsedValue) ? 0 : parsedValue;
    setTempValue([tempValue[0], valueToSet]);
    // Format for display
    setRawMax(new Intl.NumberFormat('en-US').format(valueToSet).toString());
  };

  useEffect(() => {
    setCurrentLabel(`$${formatValue(value[0])} to $${formatValue(value[1])}`);
    setTempValue(value);
    setRawMin(new Intl.NumberFormat('en-US').format(value[0]).toString());
    setRawMax(new Intl.NumberFormat('en-US').format(value[1]).toString());
  }, [value]);

  return (
    <OutsideClickHandler onOutsideClick={() => setIsDropDownVisible(false)}>
      <div
        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
          if (onFocus) {
            onFocus(e);
          }
        }}
        className={[styles.container].join(' ')}
      >
        <input
          tabIndex={0}
          readOnly
          type="text"
          name={name}
          onBlur={() => {}}
          onChange={() => {
            if (disabled) return;
            setIsDropDownVisible(!isDropDownVisible);
          }}
          id={id}
          ref={inputEl}
        />
        <div
          id={`select-${name}`}
          className={[styles.slider, styles[getErrorClass()], styles[getActiveClass()]].join(' ')}
          onClick={toggleMenuVisibility}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleMenuVisibility();
            }
          }}
          role="button"
          style={{ borderColor }}
          tabIndex={0}
        >
          <Box gap="sm" ai="center">
            <div className={styles[getLabelClass()]} ref={inputLabelArea}>
              {currentLabel}
            </div>
          </Box>
          <Icon icon="angleDown" size="xsm" />
        </div>

        <div className={[styles[getVisibilityClass()], styles.sliderDropDown].join(' ')}>
          <div className={styles.dropdownContainer}>
            <div className={styles.dropdownContainerValuesTags}>
              <div style={{ color: 'gray', marginRight: '2px' }}>Min $</div>
              <input
                value={rawMin}
                onChange={handleMinInputChange}
                onBlur={handleMinInputBlur}
                style={{
                  color: 'gray',
                  width: '80px',
                  border: '2px solid #d8d8d8',
                  borderRadius: '4px',
                  padding: '2px',
                }}
              />
            </div>

            <div className={styles.dropdownContainerValuesTags}>
              <div style={{ color: 'gray', marginRight: '2px' }}>Max $</div>
              <input
                value={rawMax}
                onChange={handleMaxInputChange}
                onBlur={handleMaxInputBlur}
                style={{
                  color: 'gray',
                  width: '80px',
                  border: '2px solid #d8d8d8',
                  borderRadius: '4px',
                  padding: '2px',
                }}
              />
            </div>
          </div>

          <div style={{ paddingTop: '20px', paddingBottom: '20px' }}>
            <SliderComponent
              style={{ width: '100%' }}
              value={tempValue}
              min={Number.isNaN(min) ? 0 : min}
              max={Number.isNaN(max) ? 0 : max}
              onChange={handleChange}
              range
              styles={{
                track: { backgroundColor: 'black', height: 10 },
                rail: { backgroundColor: 'lightgray', height: 10 },
                handle: {
                  borderColor: 'black',
                  height: 20,
                  width: 20,
                  backgroundColor: 'black',
                  opacity: 1,
                  boxShadow: 'none',
                },
              }}
            />
          </div>
          <div className={styles.dropdownContainer}>
            <Button id={`slider-reset-button-${name}`} label="Reset" color="none" size="sm" onClick={handleReset} />

            <Button
              id={`slider-apply-button-${name}`}
              label="Apply"
              color="primary"
              size="sm"
              onClick={handleOnApply}
            />
          </div>
        </div>
      </div>
    </OutsideClickHandler>
  );

  function getActiveClass(): string {
    return disabled ? '' : 'active';
  }

  function getErrorClass() {
    return error !== '' && error !== null ? 'hasError' : '';
  }

  function getVisibilityClass() {
    return isDropDownVisible ? 'visible' : 'hidden';
  }

  function getLabelClass() {
    return currentLabel ? 'label' : 'placeholder';
  }
};

export default RangeSlider;
